/*
 * MySchoolRx Workspace — login-gated tenant workspace.
 *
 * Routes (defined in App.js):
 *   /diagnostic            → DiagnosticDashboard
 *   /diagnostic/onboarding → DiagnosticOnboarding
 *   /diagnostic/form       → DiagnosticForm
 *
 * Auth: localStorage diag_session set by /diagnostic/login. If missing,
 * every component redirects to login.
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { RiskChip } from "@/components/RiskChip";
import { BandTrendBadge } from "@/components/BandTrendBadge";
import { band as bandOf, BAND_LABELS } from "@/lib/riskBand";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  ShieldCheck,
  LayoutDashboard,
  ClipboardCheck,
  CheckCircle2,
  AlertTriangle,
  Save,
  ChevronRight,
  Lock as LockIcon,
  Download,
  UserPlus,
  History,
  LogOut,
  Plus,
  Sparkles,
  Trash2,
  FlaskConical,
  Info,
  Settings as SettingsIcon,
  ArrowRight,
  Menu as MenuIcon,
  X as CloseIcon,
  ClipboardList,
  Star,
  MessageSquareText,
  ArrowLeft,
  Building2,
  Eye,
  Users as UsersIcon,
} from "lucide-react";
import { FeedbackModal } from "@/components/FeedbackModal";
import { FoundingPartnerAccess } from "@/components/FoundingPartnerAccess";
import { TermsAcceptanceGate } from "./TermsComponents";
import ServicesCTACard from "./ServicesCTACard";
import { SchoolUserMenu } from "@/components/SchoolUserMenu";
import SandboxPanel from "@/components/SandboxPanel";
import IntelligencePanel from "@/components/IntelligencePanel";
import ExplainButton from "@/components/ExplainButton";
import { WelcomeSection } from "@/components/WelcomeSection";
import { getImpersonation, exitImpersonation } from "@/lib/impersonation";
import { healthColorForScore, TILE_TINT } from "@/lib/pillarTone";
import { PillarHeatMapV2 } from "@/components/PillarHeatMapV2";
import { DiagnosticHistoryV2 } from "@/components/DiagnosticHistoryV2";
import { ScoringVersionNote } from "@/components/ScoringVersionNote";

const API = process.env.REACT_APP_BACKEND_URL;

/* ── Session helpers ── */
const getSession = () => {
  try {
    return JSON.parse(localStorage.getItem("diag_session") || "null");
  } catch {
    return null;
  }
};
const clearSession = () => localStorage.removeItem("diag_session");
const getHdrs = () => {
  const s = getSession();
  if (!s?.user?.id || !s?.tenant?.id) return null;
  const h = {
    "x-tenant-id": s.tenant.id,
    "x-user-id": s.user.id,
    "x-user-role": s.user.role || "tenant_admin",
  };
  // Server-validated identity: when present, get_ctx trusts this real session
  // token over the client-asserted x-user-* headers.
  if (s.session_token) h["x-session-token"] = s.session_token;
  return h;
};

const useRequireAuth = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (!getSession()) navigate("/diagnostic/login", { replace: true });
  }, [navigate]);
};

const RISK_COLORS = {
  // 5/30 update: collapse to Green / Yellow / Red traffic-light scheme only
  "Strong / Board-ready":                       "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]",
  "Stable but needs targeted improvement":      "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]",
  "Moderate risk":                              "text-[#a16207] bg-[#fefce8] border-[#fde047]",
  "High risk":                                  "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]",
  "Immediate corrective action needed":         "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]",
};

const STATUS_LABEL = {
  not_started: "Not Started",
  in_progress: "In Progress",
  completed: "Completed",
  submitted_for_review: "Submitted for Approval",
  approved: "Approved · Locked",
};

/* ── Sidebar ── */
const DiagSidebar = ({ navigate, route, tenant, user }) => {
  const items = [
    { key: "dashboard",  label: "Dashboard",  icon: LayoutDashboard, path: "/diagnostic" },
    { key: "diagnostic", label: "Diagnostic", icon: ClipboardCheck,  path: "/diagnostic/form" },
  ];
  // Settings tab is gated to tenant_admin / admin / super_admin
  const isTenantAdmin = ["tenant_admin", "admin", "super_admin"].includes(user?.role);
  if (isTenantAdmin) {
    items.push({ key: "users", label: "Users", icon: UsersIcon, path: "/diagnostic/users" });
    items.push({ key: "settings", label: "Settings", icon: SettingsIcon, path: "/diagnostic/settings" });
  }
  return (
    <aside aria-label="Workspace" className="hidden lg:flex flex-col w-[240px] shrink-0 bg-white text-[#111827] border-r border-[#e5e7eb] sticky top-0 h-screen overflow-y-auto" data-testid="diag-sidebar">
      <div className="px-4 pt-4 pb-4 border-b border-[#e5e7eb]">
        <div className="flex items-center">
          <img
            src="/assets/myschoolrx-wordmark-600.png"
            alt="MySchoolRx"
            className="block w-[156px] h-auto object-contain"
            data-testid="diag-sidebar-logo"
          />
        </div>
        {tenant?.school_name && (
          <div className="mt-2 px-1 text-[13px] text-[#4b5563] truncate" title={tenant.school_name} data-testid="diag-sidebar-school">
            {tenant.school_name}
          </div>
        )}
      </div>
      <nav aria-label="Main" className="flex-1 px-3 py-4 space-y-1">
        {items.map((it) => {
          const Icon = it.icon;
          const active = route === it.path || (it.path === "/diagnostic" && route === "/diagnostic/onboarding");
          return (
            <button
              key={it.key}
              onClick={() => navigate(it.path)}
              aria-current={active ? "page" : undefined}
              className={`relative w-full flex items-center gap-2.5 px-3 rounded-md text-[15px] font-semibold min-h-[44px] transition-colors ${
                active
                  ? "bg-[#e6f4f8] text-[#00698a] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:bg-[#007a9e] before:rounded-r"
                  : "text-[#374151] hover:bg-[#f3f4f6]"
              }`}
              data-testid={`diag-sidebar-${it.key}`}
            >
              <Icon className="h-[18px] w-[18px]" />
              {it.label}
            </button>
          );
        })}
      </nav>
      <div className="px-5 py-4 border-t border-[#e5e7eb] text-[11px] text-[#4b5563]">
        Diagnostic Workspace
      </div>
    </aside>
  );
};

/* ── Top bar with school name + active user + sign-out ── */
const FoundingPartnerCard = () => {
  const [fbOpen, setFbOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("fp_card_dismissed") === "1"; } catch { return false; }
  });
  const s = getSession();
  if (!s?.tenant?.is_pilot || dismissed) return null;
  const dismiss = () => { try { localStorage.setItem("fp_card_dismissed", "1"); } catch {} setDismissed(true); };
  return (
    <div className="mb-5 rounded-xl border border-[#fcd34d] bg-gradient-to-r from-[#fffbeb] to-white p-4 sm:p-5 flex items-start gap-4" data-testid="fp-dashboard-card">
      <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#f59e0b]/15 text-[#b45309]">
        <Star className="h-5 w-5 fill-[#f59e0b] text-[#f59e0b]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[15px] font-bold text-[#111827]">You're helping shape MySchoolRx.</div>
        <div className="text-[13px] text-[#6b7280] mt-0.5">Your feedback as a Founding Partner directly influences the platform's development.</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" className="bg-[#0b1220] hover:bg-black text-white" onClick={() => setFbOpen(true)} data-testid="fp-card-feedback">
          <MessageSquareText className="h-4 w-4 mr-1.5" /> Share Feedback
        </Button>
        <button onClick={dismiss} className="text-[#9ca3af] hover:text-[#6b7280]" aria-label="Dismiss" data-testid="fp-card-dismiss"><CloseIcon className="h-4 w-4" /></button>
      </div>
      <FeedbackModal open={fbOpen} onOpenChange={setFbOpen} authHeaders={getHdrs()} portalType="School User" />
    </div>
  );
};

const TopBar = ({ tenant, user, onSignOut }) => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [fbOpen, setFbOpen] = useState(false);
  const drawerToggleRef = useRef(null);
  const drawerCloseRef = useRef(null);
  const imp = getImpersonation();
  // Mirror the sidebar items so the mobile drawer has the same nav set
  const isTenantAdmin = ["tenant_admin", "admin", "super_admin"].includes(user?.role);
  const navItems = [
    { key: "dashboard", label: "Dashboard", path: "/diagnostic", icon: LayoutDashboard },
    { key: "diagnostic", label: "Diagnostic", path: "/diagnostic/form", icon: ClipboardList },
    ...(isTenantAdmin ? [{ key: "users", label: "Users", path: "/diagnostic/users", icon: UsersIcon }] : []),
    ...(isTenantAdmin ? [{ key: "settings", label: "Settings", path: "/diagnostic/settings", icon: SettingsIcon }] : []),
  ];
  const closeDrawer = () => { setMobileOpen(false); if (drawerToggleRef.current) drawerToggleRef.current.focus(); };
  // Lock body scroll, close on Esc, and manage focus while drawer is open
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") closeDrawer(); };
    document.addEventListener("keydown", onKey);
    if (drawerCloseRef.current) drawerCloseRef.current.focus();
    return () => { document.body.style.overflow = prev; document.removeEventListener("keydown", onKey); };
  }, [mobileOpen]);
  return (
    <>
    <div className="bg-white border-b border-[#e5e7eb]" data-testid="diag-topbar">
      <div className="px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {/* Mobile menu trigger — only visible below lg */}
          <button
            type="button"
            ref={drawerToggleRef}
            onClick={() => setMobileOpen(true)}
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-[#e5e7eb] text-[#111827] shrink-0"
            aria-label="Open menu"
            aria-expanded={mobileOpen ? "true" : "false"}
            aria-controls="diag-mobile-menu"
            data-testid="diag-topbar-hamburger"
          >
            <MenuIcon className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">School</div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-[15px] sm:text-[16px] font-bold text-[#111827] truncate" data-testid="diag-topbar-school">
                {tenant?.school_name || tenant?.name || "—"}
              </div>
              {tenant?.is_pilot && (
                <span
                  className="hidden sm:inline-flex items-center gap-1 rounded-full border border-[#fcd34d] bg-[#fffbeb] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#92400e]"
                  title="You're a Founding Partner helping shape MySchoolRx"
                  data-testid="founding-partner-badge"
                >
                  <Star className="h-3 w-3 fill-[#f59e0b] text-[#f59e0b]" /> Founding Partner
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {imp.active && (
            <Button
              variant="outline" size="sm"
              className="inline-flex bg-white border-[#d1d5db] text-[#111827] hover:bg-[#f9fafb] h-10 rounded-[10px]"
              onClick={exitImpersonation}
              data-testid="diag-topbar-return-admin"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> {imp.isDemo ? "Return to Admin" : "Exit Preview"}
            </Button>
          )}
          <div className="hidden md:block"><FoundingPartnerAccess headers={getHdrs()} /></div>
          <Button
            variant="outline" size="sm"
            className="hidden sm:inline-flex bg-white border-[#d1d5db] text-[#111827] hover:bg-[#f9fafb] h-10 rounded-[10px]"
            onClick={() => setFbOpen(true)}
            data-testid="diag-topbar-feedback"
          >
            <MessageSquareText className="h-4 w-4 mr-1.5" /> Give Feedback
          </Button>
          <SchoolUserMenu user={user} headers={getHdrs()} onSignOut={onSignOut} accent="#007a9e" />
        </div>
      </div>
    </div>
    <FeedbackModal open={fbOpen} onOpenChange={setFbOpen} authHeaders={getHdrs()} portalType="School User" />

      {/* Mobile drawer — rendered as a fixed sibling so it escapes the topbar's
          stacking context. z-[60] sits above any sticky chrome on the page. */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" id="diag-mobile-menu" data-testid="diag-mobile-menu" role="dialog" aria-modal="true" aria-label="Menu" onKeyDown={(e) => { if (e.key === "Escape") closeDrawer(); }}>
          <div className="absolute inset-0 bg-black/55" onClick={closeDrawer} />
          <div className="absolute top-0 left-0 h-full w-[300px] max-w-[88vw] bg-white text-[#111827] shadow-2xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between h-16 px-4 border-b border-[#e5e7eb] shrink-0">
              <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="block h-9 w-auto" />
              <button ref={drawerCloseRef} onClick={closeDrawer} className="h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-[#f3f4f6]" aria-label="Close menu" data-testid="diag-mobile-menu-close">
                <CloseIcon className="h-5 w-5" />
              </button>
            </div>
            {tenant?.school_name && (
              <div className="px-4 py-3 text-[12px] text-[#4b5563] truncate border-b border-[#e5e7eb] shrink-0">{tenant.school_name}</div>
            )}
            <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 py-3 space-y-1" data-testid="diag-mobile-nav">
              {navItems.map((it) => {
                const Icon = it.icon;
                return (
                  <button
                    key={it.key}
                    onClick={() => { closeDrawer(); navigate(it.path); }}
                    className="w-full flex items-center gap-2.5 px-3 py-3 rounded-md text-[15px] font-semibold text-[#374151] hover:bg-[#f3f4f6] min-h-[44px]"
                    data-testid={`diag-mobile-nav-${it.key}`}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                    {it.label}
                  </button>
                );
              })}
            </nav>
            {imp.active && (
              <div className="px-3 py-3 border-t border-[#e5e7eb] shrink-0">
                <Button variant="outline" size="sm" className="w-full bg-white border-[#d1d5db] text-[#111827] hover:bg-[#f9fafb] h-10 rounded-[10px]" onClick={() => { closeDrawer(); exitImpersonation(); }}>
                  <ArrowLeft className="h-4 w-4 mr-1.5" /> {imp.isDemo ? "Return to Admin" : "Exit Preview"}
                </Button>
              </div>
            )}
            <div className="px-4 py-3 border-t border-[#e5e7eb] text-[11px] text-[#4b5563] shrink-0">Diagnostic Workspace</div>
          </div>
        </div>
      )}
    </>
  );
};

/* ── Portfolio "Open School" context banner (Phase 4) ──
   Shown only when a Portfolio user has entered a school workspace via Open
   School. Keeps their identity; offers a one-click Return to Portfolio. */
const PortfolioContextBanner = () => {
  const navigate = useNavigate();
  const session = getSession();
  if (!session || session.acting_as !== "portfolio") return null;
  const pc = session.portfolio_context || {};
  const readonly = session.access === "read";
  const schoolName = session.tenant?.school_name || session.tenant?.name || "—";
  const returnToPortfolio = async () => {
    try {
      await axios.post(`${API}/api/portfolio/school-session/close`,
        { session_token: session.session_token }, { withCredentials: true });
    } catch { /* best-effort revoke */ }
    clearSession();
    navigate("/portfolio/schools", { replace: true });
  };
  return (
    <div className="bg-[#0b1220] text-white" data-testid="portfolio-context-banner">
      <div className="px-4 sm:px-6 lg:px-10 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        <span className="inline-flex items-center gap-1.5 font-semibold text-[#bfe3ee]">
          <Building2 className="h-4 w-4" /> Portfolio view
        </span>
        <span className="text-white/70 truncate">
          {pc.portfolio_name || "Portfolio"} <span className="text-white/40">▸</span>{" "}
          <span className="text-white font-medium">{schoolName}</span>
        </span>
        {readonly && (
          <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
            data-testid="portfolio-context-readonly">
            <Eye className="h-3 w-3" /> Read-only
          </span>
        )}
        <button onClick={returnToPortfolio} data-testid="portfolio-return-btn"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-white/10 hover:bg-white/20 px-3 py-1 font-medium">
          <ArrowLeft className="h-3.5 w-3.5" /> Return to Portfolio
        </button>
      </div>
    </div>
  );
};

export const Shell = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = getSession();
  const [tenantInfo, setTenantInfo] = useState(null);

  useEffect(() => {
    const h = getHdrs();
    if (!h) return;
    axios.get(`${API}/api/diagnostics/tenant/info`, { headers: h })
      .then((r) => setTenantInfo(r.data))
      .catch(() => {});
  }, []);

  const signOut = () => {
    const tok = session?.session_token;
    clearSession();
    // Revoke the school session server-side and destroy any ambient platform cookie.
    try { axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true, headers: tok ? { "x-session-token": tok } : {} }); } catch {}
    toast.message("Signed out");
    navigate("/diagnostic/login", { replace: true });
  };

  const tenant = {
    ...(session?.tenant || {}),
    ...(tenantInfo?.tenant || {}),
    // Keep the Founding Partner flag if either source has it (tenant/info wins, session is fallback).
    is_pilot: (tenantInfo?.tenant?.is_pilot ?? session?.tenant?.is_pilot) || false,
    pilot_status: tenantInfo?.tenant?.pilot_status ?? session?.tenant?.pilot_status ?? null,
  };
  const user = tenantInfo?.user || session?.user;

  return (
    <TermsAcceptanceGate productType="caporilli_diagnostic">
      <div className="min-h-screen bg-[#f9fafb] flex">
        <DiagSidebar navigate={navigate} route={location.pathname} tenant={tenant} user={user} />
        <main id="main" className="flex-1 min-w-0 flex flex-col">
          <PortfolioContextBanner />
          <TopBar tenant={tenant} user={user} onSignOut={signOut} />
          <LicenseStatusBanner />
          <div className="flex-1 pb-[120px]">{children}</div>
        </main>
      </div>
    </TermsAcceptanceGate>
  );
};

/* ── License banner — surfaces grace period / expiration state from /api/license/.../me ── */
const LicenseStatusBanner = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    (async () => {
      const h = getHdrs();
      if (!h) return;
      try {
        const r = await axios.get(`${API}/api/license/caporilli_diagnostic/me`, { headers: h });
        setInfo(r.data || null);
      } catch { /* ignore */ }
    })();
  }, []);
  if (!info || !info.license) return null;
  const eff = info.effective_status;
  if (["active", "manually_activated"].includes(eff)) return null;
  const isHard = ["expired", "cancelled"].includes(eff);
  const isSoft = ["grace_period", "renewal_due", "pending_payment"].includes(eff);
  if (!isHard && !isSoft) return null;
  const endDate = info.license.end_date ? new Date(info.license.end_date).toLocaleDateString() : "—";
  return (
    <div
      className={`px-4 py-2.5 text-[12px] font-medium border-b ${
        isHard
          ? "bg-[#fef2f2] border-[#fecaca] text-[#b91c1c]"
          : "bg-[#f0f9fc] border-[#bfe3ee] text-[#00607d]"
      }`}
      data-testid="license-status-banner"
    >
      <strong className="uppercase tracking-wide text-[11px]">{isHard ? "License Expired" : eff.replace("_", " ").toUpperCase()}:</strong>{" "}
      {info.reason || `Renew to keep access. License ends ${endDate}.`}
    </div>
  );
};

/* ── Onboarding ── */
export const DiagnosticOnboarding = () => {
  useRequireAuth();
  const navigate = useNavigate();
  return (
    <Shell>
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10 py-14" data-testid="diag-onboarding">
        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-8 sm:p-12 text-center">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#f0f9fc]">
            <ShieldCheck className="h-7 w-7 text-[#00698a]" />
          </div>
          <h1 className="mt-5 text-3xl sm:text-4xl font-bold text-[#111827] leading-tight">Welcome to your MySchoolRx workspace.</h1>
          <p className="mt-4 text-[15px] text-[#4b5563] leading-relaxed max-w-xl mx-auto">
            Your initial diagnostic is ready. Complete the eight categories at your own pace — invite team members,
            upload evidence, and assign owners along the way.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate("/diagnostic/form")} size="lg" className="bg-[#007a9e] hover:bg-[#00607d] text-white px-6" data-testid="diag-onboarding-primary">
              Start MySchoolRx <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            <Button onClick={() => navigate("/diagnostic")} size="lg" variant="outline" className="border-[#e5e7eb] text-[#111827] px-6">
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </Shell>
  );
};

/* ── Invite users modal ── */
const InviteUsersModal = ({ open, onClose, onChanged }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", full_name: "", role: "tenant_admin" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const h = getHdrs(); if (!h) return;
    try {
      const r = await axios.get(`${API}/api/diagnostics/tenant/users`, { headers: h });
      setUsers(r.data || []);
    } catch {}
  };
  useEffect(() => { if (open) load(); }, [open]);

  const submit = async () => {
    if (!form.email || !form.full_name) {
      toast.error("Email and full name required");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/diagnostics/tenant/users`, form, { headers: getHdrs() });
      toast.success(
        <div>
          <div className="font-semibold">Invited {form.full_name}</div>
          <div className="text-[12px] mt-1">Temp password: <code className="bg-black/10 px-1.5 py-0.5 rounded">{r.data.temp_password}</code></div>
        </div>,
        { duration: 20000 }
      );
      setForm({ email: "", full_name: "", role: "tenant_admin" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    } finally {
      setSubmitting(false);
    }
  };

  const deactivate = async (uid) => {
    if (!confirm("Deactivate this user?")) return;
    try {
      await axios.delete(`${API}/api/diagnostics/tenant/users/${uid}`, { headers: getHdrs() });
      toast.success("User deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resetPassword = async (uid, email) => {
    if (!confirm(`Generate a new temporary password for ${email}?`)) return;
    try {
      const r = await axios.post(`${API}/api/diagnostics/tenant/users/${uid}/reset-password`, {}, { headers: getHdrs() });
      toast.success(
        <div className="flex flex-col gap-1">
          <span className="font-semibold">Password reset for {r.data.email}</span>
          <div className="text-[12px]">Temp password: <code className="bg-black/10 px-1.5 py-0.5 rounded">{r.data.temp_password}</code></div>
          <button
            onClick={() => navigator.clipboard?.writeText(`Login: /diagnostic/login\nEmail: ${r.data.email}\nTemp password: ${r.data.temp_password}`)}
            className="text-[11px] text-[#374151] underline text-left"
          >
            Copy login details
          </button>
        </div>,
        { duration: 25000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl bg-white" data-testid="diag-invite-modal">
        <DialogHeader>
          <DialogTitle>Workspace Users</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg border border-[#e5e7eb] divide-y divide-[#e5e7eb]">
            {users.length === 0 && (
              <div className="p-4 text-sm text-[#6b7280] text-center">No users yet.</div>
            )}
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 p-3" data-testid={`diag-user-row-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-bold text-[#111827] truncate">{u.full_name}</div>
                  <div className="text-[11px] text-[#6b7280] truncate">{u.email} · {u.role}</div>
                </div>
                {u.is_active !== false ? (
                  <div className="flex items-center gap-3 shrink-0">
                    <button
                      onClick={() => resetPassword(u.id, u.email)}
                      className="text-[11px] text-[#1a56db] hover:underline"
                      data-testid={`diag-user-reset-${u.id}`}
                    >
                      Reset password
                    </button>
                    <button
                      onClick={() => deactivate(u.id)}
                      className="text-[11px] text-[#b91c1c] hover:underline"
                      data-testid={`diag-user-deactivate-${u.id}`}
                    >
                      Deactivate
                    </button>
                  </div>
                ) : (
                  <span className="text-[11px] text-[#9ca3af]">Inactive</span>
                )}
              </div>
            ))}
          </div>

          <div className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3 space-y-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6b7280]">Invite a new user</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-white border-[#e5e7eb] h-10 text-[13px]" data-testid="diag-invite-email" />
              <Input placeholder="Full name" value={form.full_name} onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} className="bg-white border-[#e5e7eb] h-10 text-[13px]" data-testid="diag-invite-name" />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="bg-white border border-[#e5e7eb] rounded-md text-[13px] h-10 px-2 flex-1"
                data-testid="diag-invite-role"
              >
                <option value="tenant_admin">School Admin (can approve)</option>
                <option value="contributor">Contributor (read+answer only)</option>
              </select>
              <Button onClick={submit} disabled={submitting} className="bg-[#0b1220] hover:bg-black text-white h-10" data-testid="diag-invite-submit">
                <UserPlus className="h-4 w-4 mr-1" /> Invite
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Period label line + inline rename (Phase 3.1) ── */
const PeriodLabelLine = ({ diag, locked, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(diag?.period_label || "");
  useEffect(() => { setVal(diag?.period_label || ""); }, [diag?.id, diag?.period_label]);
  const needsName = !!diag?.needs_period_name;

  if (editing && !locked) {
    return (
      <div className="flex items-center gap-2 mt-1" data-testid="diag-period-rename">
        <Input
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="e.g. Q2 2026, Mid-Year Review, Pre-Renewal Check…"
          className="bg-white border-[#e5e7eb] text-[14px] h-9 max-w-[360px]"
          data-testid="diag-period-input"
        />
        <Button size="sm" disabled={!val.trim()} onClick={async () => { await onRename(val.trim()); setEditing(false); }}
          className="bg-[#007a9e] hover:bg-[#00607d] text-white h-9 text-[12px]" data-testid="diag-period-save">
          Save
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setVal(diag.period_label); setEditing(false); }}
          className="border-[#e5e7eb] h-9 text-[12px]" data-testid="diag-period-cancel">
          Cancel
        </Button>
      </div>
    );
  }
  return (
    <p className="text-sm text-[#6b7280] flex items-center gap-2 flex-wrap" data-testid="diag-period-line">
      <strong className={needsName ? "text-[#b45309]" : "text-[#374151]"}>
        {needsName ? "Name this diagnostic period →" : diag.period_label}
      </strong>
      {!locked && (
        <button onClick={() => setEditing(true)}
          className="text-[11px] font-semibold text-[#3B4FE4] hover:underline"
          data-testid="diag-period-rename-btn">
          {needsName ? "Pick a name" : "Rename"}
        </button>
      )}
      {diag.completed_at && <span>· Completed {new Date(diag.completed_at).toLocaleDateString()}</span>}
      {diag.approved_at && <span>· Approved {new Date(diag.approved_at).toLocaleDateString()}</span>}
    </p>
  );
};

/* ── New diagnostic modal ── */
const NewDiagnosticModal = ({ open, onClose, onCreated, existingPeriods = [] }) => {
  const now = new Date();
  const currentQ = Math.floor(now.getMonth() / 3) + 1;
  const buildQuarter = (q, y) => `Q${q} ${y}`;
  // Suggest the first quarter (current → next 4) that doesn't already exist
  const suggestions = useMemo(() => {
    const list = [];
    let q = currentQ, y = now.getFullYear();
    for (let i = 0; i < 5; i++) {
      list.push(buildQuarter(q, y));
      q += 1;
      if (q > 4) { q = 1; y += 1; }
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const firstAvailable = suggestions.find((s) => !existingPeriods.includes(s)) || suggestions[0];
  const [periodType, setPeriodType] = useState("quarterly");
  const [periodLabel, setPeriodLabel] = useState(firstAvailable);
  const [customLabel, setCustomLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Reset to a fresh suggestion every time the modal opens
  useEffect(() => {
    if (open) {
      setPeriodLabel(firstAvailable);
      setPeriodType("quarterly");
      setCustomLabel("");
    }
  }, [open, firstAvailable]);

  const create = async () => {
    const label = periodType === "custom" ? customLabel.trim() : (periodLabel || "").trim();
    if (!label) {
      toast.error("Please enter a Diagnostic Period Name before starting the diagnostic.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/api/diagnostics`, {
        period_label: label,
        period_type: periodType,
      }, { headers: getHdrs() });
      toast.success(`Created "${label}" diagnostic`);
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="diag-new-modal">
        <DialogHeader>
          <DialogTitle>Start a New Diagnostic</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1.5">Period type</label>
            <div className="flex gap-2">
              {[
                { v: "quarterly", l: "Quarterly" },
                { v: "custom", l: "Custom" },
              ].map((o) => (
                <button
                  key={o.v}
                  onClick={() => setPeriodType(o.v)}
                  className={`px-3 py-1.5 rounded-md text-[13px] font-semibold border ${
                    periodType === o.v ? "bg-[#0b1220] text-white border-[#0b1220]" : "bg-white text-[#374151] border-[#e5e7eb]"
                  }`}
                  data-testid={`diag-new-period-${o.v}`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          {periodType === "quarterly" ? (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1.5">Quarter</label>
              <select
                value={periodLabel}
                onChange={(e) => setPeriodLabel(e.target.value)}
                className="bg-white border border-[#e5e7eb] rounded-md text-[13px] h-10 px-2 w-full"
                data-testid="diag-new-quarter"
              >
                {suggestions.map((s) => (
                  <option key={s} value={s} disabled={existingPeriods.includes(s)}>
                    {s}{existingPeriods.includes(s) ? "  (already created)" : ""}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6b7280] mb-1.5">Custom label</label>
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="e.g., Mid-Year Self-Assessment"
                className="bg-white border-[#e5e7eb] h-10 text-[13px]"
                data-testid="diag-new-custom"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={submitting} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="diag-new-submit">
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ── Trend chart (lightweight SVG) ── */


/* ── What-If Scenario Simulator ── */
const WhatIfPanel = ({ diagnosticId, baselineSummary }) => {
  const [schema, setSchema] = useState({ categories: [], questions: {} });
  const [responses, setResponses] = useState([]);
  const [overrides, setOverrides] = useState({});  // key: "cat::idx" → newValue
  const [scenarioName, setScenarioName] = useState("Scenario A");
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    const h = getHdrs(); if (!h) return;
    try {
      const [s, full, scns] = await Promise.all([
        axios.get(`${API}/api/diagnostics/categories`),
        axios.get(`${API}/api/diagnostics/${diagnosticId}`, { headers: h }),
        axios.get(`${API}/api/diagnostics/${diagnosticId}/scenarios`, { headers: h }),
      ]);
      setSchema(s.data);
      setResponses(full.data.responses || []);
      setSavedScenarios(scns.data || []);
    } catch (e) {
      // soft fail
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [diagnosticId]);

  // Weakest 10 currently-scored responses
  const weakest = useMemo(() => {
    return responses
      .filter((r) => r.response_value != null && !r.is_not_applicable)
      .sort((a, b) => (a.response_value - b.response_value))
      .slice(0, 10);
  }, [responses]);

  // Simulated summary computed client-side (mirrors backend logic)
  const simulated = useMemo(() => {
    if (Object.keys(overrides).length === 0) return null;
    const byCat = {};
    const overrideMap = overrides;
    for (const r of responses) {
      const k = `${r.category}::${r.question_index}`;
      const val = (overrideMap[k] != null) ? overrideMap[k] : r.response_value;
      if (val == null || r.is_not_applicable) continue;
      (byCat[r.category] = byCat[r.category] || []).push(val);
    }
    // also include overrides for unanswered questions
    for (const [k, v] of Object.entries(overrideMap)) {
      const [cat, idxStr] = k.split("::");
      const idx = parseInt(idxStr, 10);
      const exists = responses.some((r) => r.category === cat && r.question_index === idx);
      if (!exists) (byCat[cat] = byCat[cat] || []).push(v);
    }
    const cats = schema.categories.map((c) => {
      const arr = byCat[c.id] || [];
      const avg = arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length) : null;
      return { id: c.id, title: c.title, score: avg != null ? Math.round(avg * 20 * 10) / 10 : null };
    });
    const scored = cats.filter((c) => c.score != null);
    const overall = scored.length ? Math.round(scored.reduce((a, c) => a + c.score, 0) / scored.length * 10) / 10 : null;
    return { categories: cats, overall };
  }, [overrides, responses, schema.categories]);

  const updateOverride = (cat, idx, val) => {
    const k = `${cat}::${idx}`;
    setOverrides((prev) => {
      const next = { ...prev };
      if (val == null) delete next[k];
      else next[k] = val;
      return next;
    });
  };

  const saveScenario = async () => {
    if (!scenarioName.trim()) {
      toast.error("Scenario name required");
      return;
    }
    const arr = Object.entries(overrides).map(([k, v]) => {
      const [cat, idxStr] = k.split("::");
      return { category: cat, question_index: parseInt(idxStr, 10), response_value: v };
    });
    if (arr.length === 0) {
      toast.error("Add at least one override");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/diagnostics/${diagnosticId}/scenarios`, {
        name: scenarioName.trim(),
        overrides: arr,
      }, { headers: getHdrs() });
      toast.success("Scenario saved");
      setOverrides({});
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const deleteScenario = async (id) => {
    if (!confirm("Delete this scenario?")) return;
    try {
      await axios.delete(`${API}/api/diagnostics/${diagnosticId}/scenarios/${id}`, { headers: getHdrs() });
      toast.success("Deleted");
      loadAll();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const baseScore = baselineSummary?.overall_score ?? null;
  const simScore = simulated?.overall ?? null;
  const delta = (simScore != null && baseScore != null) ? Math.round((simScore - baseScore) * 10) / 10 : null;

  // 6/4 — risk band helper for Current vs Simulated label
  const riskFor = (s) => {
    if (s == null) return "—";
    if (s >= 80) return "Healthy";
    if (s >= 60) return "Caution";
    return "High Risk";
  };
  const baseRisk = riskFor(baseScore);
  const simRisk = simScore != null ? riskFor(simScore) : null;

  // 6/4 — open/reset are local UX state; not auditing client-side beacons.

  const resetSim = () => {
    setOverrides({});
  };

  return (
    <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="diag-whatif-panel">
      {/* 6/4 — explanatory header so users understand this is a sandbox.
          The simulator never mutates the approved diagnostic; reset clears local overrides. */}
      <div className="mb-4 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-4 py-3" data-testid="diag-whatif-intro">
        <div className="flex items-start gap-2">
          <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-white bg-[#1d4ed8] px-2 py-0.5 rounded shrink-0">Simulation only</span>
          <p className="text-[13px] text-[#1e3a8a] leading-snug">
            Use the What-If Simulator to model how potential improvements could affect your diagnostic score
            before making changes to the official record. <strong>Simulated changes do not update your approved
            diagnostic or your board-ready report.</strong> Use Reset Simulation to clear your scenario at any time.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-[#111827] flex items-center gap-1.5">
            <FlaskConical className="h-4 w-4" /> What-If Simulator
          </h3>
          <p className="text-sm text-[#6b7280]">
            Move a weak response up to see the projected new overall, pillar movement, and risk-level shift.
          </p>
        </div>
        <div className="flex items-center gap-3 text-[12px]" data-testid="diag-whatif-score-block">
          <div className="text-[#6b7280]">
            Current Score: <strong className="text-[#111827]" data-testid="diag-whatif-current-score">{baseScore ?? "—"}/100</strong>
            <span className="ml-1 text-[10px] text-[#9ca3af]">({baseRisk})</span>
          </div>
          {simScore != null && (
            <>
              <div className="text-[#6b7280]">
                Simulated Score: <strong className="text-[#111827]" data-testid="diag-whatif-sim-score">{simScore}/100</strong>
                <span className="ml-1 text-[10px] text-[#9ca3af]">({simRisk})</span>
              </div>
              {delta != null && (
                <span
                  className={`text-[11px] font-bold px-2 py-1 rounded border ${
                    delta > 0 ? "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]"
                    : delta < 0 ? "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]"
                    : "text-[#6b7280] bg-[#f9fafb] border-[#e5e7eb]"
                  }`}
                  data-testid="diag-whatif-delta"
                >
                  {delta > 0 ? "+" : ""}{delta}
                </span>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-2" data-testid="diag-whatif-list">
        {weakest.length === 0 && (
          <div className="text-sm text-[#6b7280] py-2">Answer more questions to surface improvement candidates.</div>
        )}
        {weakest.map((r) => {
          const k = `${r.category}::${r.question_index}`;
          const simVal = overrides[k] != null ? overrides[k] : null;
          const catTitle = schema.categories.find((c) => c.id === r.category)?.title || r.category;
          return (
            <div key={k} className="rounded-lg border border-[#e5e7eb] bg-[#f9fafb] p-3" data-testid={`diag-whatif-row-${r.category}-${r.question_index}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">{catTitle}</div>
                  <p className="text-[13px] font-semibold text-[#111827] leading-snug mt-0.5">{r.question_text}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[11px] text-[#6b7280]">Current</span>
                  <span className="inline-flex items-center justify-center min-w-[28px] h-7 rounded-md bg-white border border-[#e5e7eb] text-[12px] font-bold text-[#111827]">{r.response_value}</span>
                  <span className="text-[11px] text-[#6b7280]">→ If</span>
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => updateOverride(r.category, r.question_index, simVal === v ? null : v)}
                      className={`min-w-[28px] h-7 rounded-md text-[12px] font-bold transition-colors ${
                        simVal === v ? "bg-[#007a9e] text-white" : "bg-white border border-[#e5e7eb] text-[#374151] hover:border-[#007a9e]/40"
                      }`}
                      data-testid={`diag-whatif-set-${r.category}-${r.question_index}-${v}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <Input
          value={scenarioName}
          onChange={(e) => setScenarioName(e.target.value)}
          placeholder="Scenario name"
          className="h-10 bg-white border-[#e5e7eb] text-[13px] max-w-[260px]"
          data-testid="diag-whatif-name"
        />
        <Button onClick={saveScenario} disabled={saving || Object.keys(overrides).length === 0} className="bg-[#0b1220] hover:bg-black text-white" data-testid="diag-whatif-save">
          {saving ? "Saving…" : "Save Scenario"}
        </Button>
        <Button
          onClick={resetSim}
          variant="outline"
          disabled={Object.keys(overrides).length === 0}
          className="border-[#e5e7eb] text-[#374151] disabled:opacity-50"
          data-testid="diag-whatif-reset"
        >
          Reset Simulation
        </Button>
        <span className="ml-1 text-[11px] text-[#9ca3af]">Saved scenarios are sandboxed; they never modify the approved diagnostic.</span>
      </div>

      {savedScenarios.length > 0 && (
        <div className="mt-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-[#6b7280] mb-2">Saved Scenarios</div>
          <div className="space-y-2" data-testid="diag-whatif-saved-list">
            {savedScenarios.map((s) => (
              <div key={s.id} className="rounded-lg border border-[#e5e7eb] bg-white p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`diag-whatif-saved-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-[13px] font-bold text-[#111827]">{s.name}</div>
                  <div className="text-[11px] text-[#6b7280]">
                    {s.overrides.length} override{s.overrides.length === 1 ? "" : "s"} ·
                    baseline {s.baseline_summary?.overall_score ?? "—"} → simulated <strong className="text-[#065f46]">{s.simulated_summary?.overall_score ?? "—"}</strong>
                  </div>
                </div>
                <button onClick={() => deleteScenario(s.id)} className="text-[#b91c1c] hover:bg-[#fef2f2] p-1 rounded" data-testid={`diag-whatif-delete-${s.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── AI Corrective Action Plan ── */
// AI Corrective Action Plan is a paid upgrade (NOT included in the base annual
// license). The Tenant sees an "Available as Upgrade" gate by default. The
// product owner can unlock the panel for demo/internal use by entering the
// override password configured via REACT_APP_CAPORILLI_AI_UNLOCK (defaults to
// "Demo589!"). Unlock state is stored in localStorage so it persists across
// reloads on the same browser; clearing localStorage relocks the panel.
const AI_PLAN_UNLOCK_PASSWORD = process.env.REACT_APP_CAPORILLI_AI_UNLOCK || "Demo589!";
const AI_PLAN_UNLOCK_KEY = "caporilli_ai_corrective_unlock";

const AICorrectiveActionUpgradeCard = ({ onUnlocked }) => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const tryUnlock = () => {
    if (pw.trim() === AI_PLAN_UNLOCK_PASSWORD) {
      localStorage.setItem(AI_PLAN_UNLOCK_KEY, "true");
      setShowModal(false);
      setPw("");
      setErr("");
      onUnlocked && onUnlocked();
    } else {
      setErr("Incorrect password.");
    }
  };
  return (
    <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-gradient-to-br from-[#f0f9fc] to-white p-5 relative overflow-hidden" data-testid="diag-actionplan-upgrade-card">
      <span className="absolute top-3 right-3 inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[#f0f9fc] text-[#00485f] border border-[#bfe3ee]">
        Available as Upgrade
      </span>
      <h3 className="text-base font-bold text-[#111827] flex items-center gap-1.5">
        <Sparkles className="h-4 w-4 text-[#00698a]" /> AI Corrective Action Plan
      </h3>
      <p className="text-sm text-[#6b7280] mt-2 max-w-xl">
        Generate a 12-week, AI-built corrective action plan from your weakest pillars — with owners,
        due dates, and weekly improvement tasks. Available as a paid upgrade to the annual license.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          onClick={() => navigate("/diagnostic/upgrade/ai-plan")}
          className="bg-[#007a9e] hover:bg-[#00607d] text-white"
          data-testid="diag-actionplan-upgrade-cta"
        >
          Request Upgrade — $1,000 <ArrowRight className="h-4 w-4 ml-1.5" />
        </Button>
        <button
          onClick={() => { setShowModal(true); setErr(""); }}
          className="text-[11px] text-[#6b7280] hover:text-[#374151] underline underline-offset-2"
          data-testid="diag-actionplan-override-link"
        >
          Have an unlock code?
        </button>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
          data-testid="diag-actionplan-unlock-modal"
        >
          <div className="bg-white rounded-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#111827]">Internal unlock</h3>
            <p className="text-[12px] text-[#6b7280] mt-1">
              Enter the demo unlock password to enable the AI Corrective Action Plan panel for this tenant on this browser only.
            </p>
            <Input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") tryUnlock(); }}
              placeholder="Unlock password"
              className="mt-3 bg-white border-[#e5e7eb] text-[14px]"
              data-testid="diag-actionplan-unlock-input"
              autoFocus
            />
            {err && <div className="mt-2 text-[12px] text-[#b91c1c]" data-testid="diag-actionplan-unlock-err">{err}</div>}
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)} className="border-[#e5e7eb]" data-testid="diag-actionplan-unlock-cancel">Cancel</Button>
              <Button onClick={tryUnlock} className="bg-[#0b1220] hover:bg-[#1f2937] text-white" data-testid="diag-actionplan-unlock-submit">Unlock</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AICorrectiveActionPlanGate = ({ diagnosticId }) => {
  const [unlocked, setUnlocked] = useState(() => {
    try { return localStorage.getItem(AI_PLAN_UNLOCK_KEY) === "true"; } catch { return false; }
  });
  // 6/3 update — per-tenant license entitlement. Super Admin can toggle
  // `ai_corrective_action_plan_enabled` on the tenant's license. When true the
  // feature is unlocked for ALL users in that tenant (no override password
  // needed). The Demo589! override remains as a backstage / demo-only path.
  const [entitled, setEntitled] = useState(false);
  useEffect(() => {
    const h = getHdrs(); if (!h) return;
    axios.get(`${API}/api/license/caporilli_diagnostic/me`, { headers: h })
      .then((r) => setEntitled(!!r.data?.ai_corrective_action_plan_enabled))
      .catch(() => setEntitled(false));
  }, []);
  if (!unlocked && !entitled) {
    return <AICorrectiveActionUpgradeCard onUnlocked={() => setUnlocked(true)} />;
  }
  return (
    <div data-testid="diag-actionplan-unlocked-wrap">
      {entitled ? (
        <div className="mt-6 flex items-center gap-2 text-[11px] text-[#065f46]" data-testid="diag-actionplan-entitled-banner">
          <Sparkles className="h-3 w-3 text-[#059669]" />
          AI Corrective Action Plan add-on active on this license.
        </div>
      ) : (
        <div className="mt-6 flex items-center gap-2 text-[11px] text-[#6b7280]" data-testid="diag-actionplan-unlocked-banner">
          <Sparkles className="h-3 w-3 text-[#00698a]" />
          AI Corrective Action Plan unlocked (demo / internal preview).
          <button
            onClick={() => {
              localStorage.removeItem(AI_PLAN_UNLOCK_KEY);
              setUnlocked(false);
            }}
            className="ml-2 underline hover:text-[#374151]"
            data-testid="diag-actionplan-relock"
          >
            Re-lock
          </button>
        </div>
      )}
      <ActionPlanPanel diagnosticId={diagnosticId} />
    </div>
  );
};


const ActionPlanPanel = ({ diagnosticId }) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const h = getHdrs(); if (!h) return;
    try {
      const r = await axios.get(`${API}/api/diagnostics/${diagnosticId}/action-plan`, { headers: h });
      setPlan(r.data || null);
    } catch {
      setPlan(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [diagnosticId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/diagnostics/${diagnosticId}/action-plan/generate`, {}, {
        headers: getHdrs(),
        timeout: 90000,
      });
      setPlan(r.data);
      toast.success("Action plan generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const cycleStatus = async (taskId, current) => {
    const next = current === "not_started" ? "in_progress" : current === "in_progress" ? "complete" : "not_started";
    try {
      await axios.patch(`${API}/api/diagnostics/${diagnosticId}/action-plan/tasks/${taskId}`, { status: next }, { headers: getHdrs() });
      setPlan((p) => p && ({
        ...p,
        weeks: p.weeks.map((w) => ({
          ...w,
          tasks: w.tasks.map((t) => t.id === taskId ? { ...t, status: next } : t),
        })),
      }));
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const activate = async () => {
    try {
      await axios.post(`${API}/api/diagnostics/${diagnosticId}/action-plan/activate`, {}, { headers: getHdrs() });
      toast.success("Plan activated");
      load();
    } catch (e) {
      toast.error("Activate failed");
    }
  };

  if (loading) {
    return <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5 text-[#6b7280] text-sm">Loading action plan…</div>;
  }

  return (
    <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="diag-actionplan-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-bold text-[#111827] flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-[#00698a]" /> AI Corrective Action Plan
          </h3>
          <p className="text-sm text-[#6b7280]">
            12-week plan generated from your weakest categories. Tasks include owners, due dates, and deliverables.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {plan && (
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border ${
              plan.status === "active" ? "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]" : "text-[#6b7280] bg-[#f9fafb] border-[#e5e7eb]"
            }`}>
              {plan.status}
            </span>
          )}
          {plan && plan.status === "draft" && (
            <Button onClick={activate} className="bg-[#059669] hover:bg-[#047857] text-white" data-testid="diag-actionplan-activate">
              Activate Plan
            </Button>
          )}
          <Button onClick={generate} disabled={generating} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="diag-actionplan-generate">
            <Sparkles className="h-4 w-4 mr-1.5" />
            {generating ? "Generating…" : plan ? "Regenerate" : "Generate Plan"}
          </Button>
        </div>
      </div>

      {!plan && !generating && (
        <div className="mt-4 text-sm text-[#6b7280]">No plan yet. Click <strong>Generate Plan</strong> to have AI build a 12-week corrective roadmap from your responses.</div>
      )}

      {plan && (
        <>
          {plan.summary && (
            <div className="mt-4 rounded-lg bg-[#f9fafb] border border-[#e5e7eb] p-3 text-[13px] text-[#374151] leading-relaxed">
              {plan.summary}
            </div>
          )}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="diag-actionplan-weeks">
            {plan.weeks.map((w) => (
              <div key={w.week} className="rounded-lg border border-[#e5e7eb] bg-white p-4" data-testid={`diag-actionplan-week-${w.week}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#00698a] font-bold">Week {w.week}</div>
                    <div className="text-[14px] font-bold text-[#111827] leading-snug">{w.focus}</div>
                  </div>
                  <div className="text-[10px] text-[#6b7280]">{new Date(w.due_date).toLocaleDateString()}</div>
                </div>
                <div className="mt-3 space-y-2">
                  {w.tasks.map((t) => {
                    const checked = t.status === "complete";
                    const inProg = t.status === "in_progress";
                    return (
                      <div key={t.id} className="rounded-md border border-[#e5e7eb] bg-[#f9fafb] p-2.5" data-testid={`diag-actionplan-task-${t.id}`}>
                        <div className="flex items-start gap-2">
                          <button
                            type="button"
                            onClick={() => cycleStatus(t.id, t.status)}
                            className={`mt-0.5 h-5 w-5 rounded border flex items-center justify-center shrink-0 ${
                              checked ? "bg-[#059669] border-[#059669]" : inProg ? "bg-[#007a9e] border-[#007a9e]" : "bg-white border-[#9ca3af]"
                            }`}
                            data-testid={`diag-actionplan-task-toggle-${t.id}`}
                            title={t.status}
                          >
                            {checked && <CheckCircle2 className="h-4 w-4 text-white" />}
                            {inProg && <span className="block h-2 w-2 rounded-full bg-white" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <div className={`text-[13px] font-semibold leading-snug ${checked ? "line-through text-[#9ca3af]" : "text-[#111827]"}`}>
                              {t.title}
                            </div>
                            <div className="text-[11px] text-[#6b7280] mt-0.5">
                              <span className="font-semibold">{t.owner}</span> · {t.category} · {t.estimate_hours}h
                            </div>
                            {t.deliverable && (
                              <div className="text-[11px] text-[#6b7280] mt-0.5"><em>Deliverable:</em> {t.deliverable}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};


/* ── Phase 5 + 5/30 update: Pillar health colour helper + Warning Lights ──
   Strict 3-color traffic-light mapping (Green/Yellow/Red + Grey for unscored). Shared with Portfolio. */

function overallRiskBand(score) {
  if (score == null) return { label: "Not scored", tone: "grey", bg: "bg-[#f3f4f6]", border: "border-[#e5e7eb]", text: "text-[#6b7280]" };
  if (score >= 80) return { label: "Healthy", tone: "green", bg: "bg-[#f0fdf4]", border: "border-[#bbf7d0]", text: "text-[#166534]" };
  if (score >= 60) return { label: "Caution", tone: "yellow", bg: "bg-[#fffbeb]", border: "border-[#fde68a]", text: "text-[#92400e]" };
  return { label: "High Risk", tone: "red", bg: "bg-[#fef2f2]", border: "border-[#fecaca]", text: "text-[#991b1b]" };
}


/* ── Standalone Overall Risk Level banner — rendered at top of dashboard
    per 6/3 spec. Uses the same chip styling as the Warning Lights overall
    indicator so the dashboard has a single, prominent risk readout. ── */
const BAND_RING = { green: "#059669", yellow: "#ca8a04", red: "#dc2626", grey: "#94a3b8" };

const ScoreDonut = ({ score, color, provisional }) => {
  const pct = Math.max(0, Math.min(100, score ?? 0));
  const r = 42, circ = 2 * Math.PI * r;
  const off = circ - (pct / 100) * circ;
  return (
    <div className="relative h-[104px] w-[104px] shrink-0" data-testid="dashboard-score-donut"
      role="img" aria-label={`Overall score ${score != null ? score : "not scored"} out of 100${provisional ? ", provisional" : score != null ? ", final" : ""}`}>
      <svg viewBox="0 0 104 104" className="h-full w-full -rotate-90">
        <circle cx="52" cy="52" r={r} fill="none" stroke="#e5e7eb" strokeWidth="10" />
        {score != null && (
          <circle cx="52" cy="52" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
            strokeDasharray={circ} strokeDashoffset={off}
            style={{ transition: "stroke-dashoffset 700ms ease" }} />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold leading-none text-[#111827]">{score != null ? score : "—"}</span>
        <span className="text-[10px] text-[#9ca3af] font-medium">/100</span>
      </div>
    </div>
  );
};

const OverallRiskBanner = ({ summary, diagnostic, prevScore }) => {
  const score = summary?.overall_score;
  const band = overallRiskBand(score);
  const isFinal = !!(diagnostic?.locked || diagnostic?.status === "approved");
  const provisional = score != null && !isFinal;
  return (
    <div
      className={`mt-4 rounded-xl border ${band.border} ${band.bg} px-5 py-4 flex items-center justify-between gap-4`}
      data-testid="dashboard-overall-risk"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className={`text-[10px] uppercase tracking-wider font-bold ${band.text}`}>Overall Risk Level</div>
          {score != null && (
            <span data-testid="dashboard-score-status"
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${
                provisional ? "bg-white text-[#a16207] border-[#fde047]" : "bg-[#ecfdf5] text-[#065f46] border-[#a7f3d0]"
              }`}>
              {provisional ? "In Progress · Provisional" : "Final"}
            </span>
          )}
          <BandTrendBadge score={score} prevScore={prevScore} testid="dashboard-overall-trend" />
        </div>
        <div className={`text-xl font-bold mt-1 ${band.text}`}>{band.label}</div>
        {provisional && (
          <div className="text-[11px] text-[#6b7280] mt-1">Score updates as you answer — it becomes Final after submission and approval.</div>
        )}
      </div>
      <ScoreDonut score={score} color={BAND_RING[band.tone] || BAND_RING.grey} provisional={provisional} />
    </div>
  );
};

const WarningLightsSection = ({ summary, diagnostic, onJumpToCategory }) => {
  const [license, setLicense] = useState(null);
  useEffect(() => {
    const h = getHdrs(); if (!h) return;
    axios.get(`${API}/api/license/caporilli_diagnostic/me`, { headers: h })
      .then((r) => setLicense(r.data || null))
      .catch(() => { /* ignore */ });
  }, []);

  if (!summary) return null;
  const lights = [];

  // 6/3 update — show EVERY non-green pillar in Warning Lights (not just the
  // 2 weakest). Green pillars are excluded; the section caps at 8 (max pillars).
  const cats = (summary.category_summaries || []);
  const sorted = [...cats]
    .filter((c) => c.score == null || bandOf(c.score) !== "healthy")
    .sort((a, b) => (a.score ?? -1) - (b.score ?? -1));
  sorted.forEach((c) => {
    if (c.score != null && bandOf(c.score) === "healthy") return;
    const sev = (c.score == null) ? "amber"
      : bandOf(c.score) === "high" ? "red"
      : "amber";
    const title = c.score == null
      ? `${c.title} — not yet scored`
      : `${c.title} is at ${BAND_LABELS[bandOf(c.score)] || "risk"}`;
    const hint = c.score == null
      ? "Answer indicators to surface risk and improvement areas"
      : `Score ${c.score}/100 · click to address weakest indicators`;
    lights.push({
      severity: sev,
      icon: AlertTriangle,
      title,
      hint,
      onClick: () => onJumpToCategory(c.id),
      testid: `warning-pillar-${c.id}`,
    });
  });

  // Evidence gaps
  const evGapTotal = cats.reduce((acc, c) => acc + (c.evidence_gaps || 0), 0);
  if (evGapTotal >= 5) {
    lights.push({
      severity: "amber",
      icon: AlertTriangle,
      title: `${evGapTotal} evidence missing across pillars`,
      hint: "Add evidence URLs or notes to fill in missing artifacts",
      onClick: () => onJumpToCategory(sorted[0]?.id),
      testid: "warning-evidence-gaps",
    });
  }

  // License about to expire / expired
  if (license?.license && license.effective_status) {
    if (["expired", "cancelled"].includes(license.effective_status)) {
      lights.push({
        severity: "red",
        icon: AlertTriangle,
        title: "License expired",
        hint: "Renew to restore reports & official-submission rights",
        testid: "warning-license-expired",
      });
    } else if (license.license.end_date) {
      const days = Math.floor((new Date(license.license.end_date) - new Date()) / (24 * 3600 * 1000));
      if (days >= 0 && days <= 30) {
        lights.push({
          severity: "amber",
          icon: AlertTriangle,
          title: `License expires in ${days} day${days === 1 ? "" : "s"}`,
          hint: "Renew before annual report cycle to keep reporting access",
          testid: "warning-license-soon",
        });
      }
    }
  }

  // CMO/EMO not assessed (if not N/A and 0% complete)
  const cmoEmo = cats.find((c) => c.id === "cmo_emo");
  if (cmoEmo && !cmoEmo.is_not_applicable && (cmoEmo.completion_pct || 0) === 0) {
    lights.push({
      severity: "amber",
      icon: AlertTriangle,
      title: "School Management evaluation not started",
      hint: "Mark Not Applicable or start the School Management pillar",
      onClick: () => onJumpToCategory("cmo_emo"),
      testid: "warning-cmo-emo",
    });
  }

  // Cap at 5
  // Cap at 8 (max pillars). All non-green pillars are surfaced — no green hides.
  const top = lights.slice(0, 8);

  // Overall Risk chip moved to the top-level dashboard (OverallRiskBanner)
  // to avoid duplication per 6/3 update.
  const overallScore = summary.overall_score;

  if (top.length === 0) {
    // Phase distinction: only show "All clear" once the diagnostic is complete + scored.
    // For new/in-progress diagnostics show an onboarding message instead.
    const isComplete = diagnostic && (
      diagnostic.status === "completed" ||
      diagnostic.status === "submitted_for_review" ||
      diagnostic.status === "approved"
    );
    const completionPct = summary?.completion_percentage || 0;
    const scored = (overallScore != null) && isComplete && completionPct >= 100;

    if (!scored) {
      return (
        <div className="mt-6 space-y-3" data-testid="warning-lights-section">
          <div className="rounded-xl border border-[#bfdbfe] bg-[#eff6ff] px-4 py-3 flex items-start gap-2" data-testid="warning-lights-onboarding">
            <Info className="h-4 w-4 text-[#1d4ed8] mt-0.5 shrink-0" />
            <span className="text-[13px] text-[#1e3a8a]">
              <strong>Your MySchoolRx license is active.</strong>{" "}
              You can now begin your diagnostic, complete each pillar, and generate your board-ready report when finished.
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="mt-6 space-y-3" data-testid="warning-lights-section">
        <div className="rounded-xl border border-[#a7f3d0] bg-[#ecfdf5] px-4 py-3 flex items-center gap-2" data-testid="warning-lights-clear">
          <CheckCircle2 className="h-4 w-4 text-[#059669]" />
          <span className="text-[13px] text-[#065f46]"><strong>All clear.</strong> No urgent issues — every pillar is on track and your license is active.</span>
        </div>
      </div>
    );
  }
  return (
    <div className="mt-6 space-y-3" data-testid="warning-lights-section">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-bold text-[#111827] flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-[#00698a]" /> Warning Lights
        </h2>
        <span className="text-[11px] text-[#6b7280]">Top {top.length} urgent issue{top.length === 1 ? "" : "s"}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {top.map((l, i) => {
          const Icon = l.icon;
          const cls = l.severity === "red"
            ? "border-[#fecaca] bg-[#fef2f2] text-[#b91c1c]"
            : "border-[#fde047] bg-[#fefce8] text-[#a16207]";
          return (
            <button
              key={i}
              onClick={l.onClick}
              disabled={!l.onClick}
              className={`text-left rounded-xl border px-4 py-3 transition-colors ${cls} ${l.onClick ? "hover:brightness-95 cursor-pointer" : ""}`}
              data-testid={l.testid}
            >
              <div className="flex items-start gap-2">
                <Icon className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold text-[13px]">{l.title}</div>
                  <div className="text-[11px] opacity-80 mt-0.5">{l.hint}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};


/* ── Phase 5: Diagnostic requested by the Portfolio ── */
const DiagnosticRequestBanner = ({ onStarted }) => {
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    (async () => {
      const h = getHdrs(); if (!h) return;
      try {
        const r = await axios.get(`${API}/api/diagnostics/period-requests`, { headers: h });
        setReqs(r.data?.requests || []);
      } catch { /* ignore */ }
    })();
  }, []);
  const start = async (req) => {
    setBusy(req.id);
    try {
      await axios.post(`${API}/api/diagnostics/period-requests/${req.id}/start`, {}, { headers: getHdrs() });
      toast.success("Diagnostic started");
      if (onStarted) await onStarted();
      navigate("/diagnostic/form");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start the diagnostic");
      setBusy("");
    }
  };
  if (!reqs.length) return null;
  return (
    <div className="mb-5 space-y-3" data-testid="diagnostic-request-banner">
      {reqs.map((req) => (
        <div key={req.id} data-testid={`diagnostic-request-${req.id}`}
          className="rounded-xl border border-[#bfe3ee] bg-[#f0f9fc] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="h-10 w-10 shrink-0 rounded-lg grid place-items-center bg-[#007a9e] text-white">
            <ClipboardCheck className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#00607d]">
              {req.portfolio_name || "Your portfolio"} requested a diagnostic
            </div>
            <div className="text-[13px] text-[#00607d]/80">
              <strong>{req.period_name}</strong>
              {req.due_date ? <> · due {req.due_date}</> : null}
              {req.status === "started" ? " · in progress" : ""}
            </div>
          </div>
          <button onClick={() => start(req)} disabled={busy === req.id} data-testid={`diagnostic-request-start-${req.id}`}
            className="shrink-0 inline-flex items-center gap-2 rounded-md bg-[#b91c1c] hover:bg-[#991b1b] text-white text-sm font-medium px-4 py-2">
            {busy === req.id ? <Loader2Local /> : <ArrowRight className="h-4 w-4" />}
            {req.status === "started" ? "Continue Diagnostic" : "Start Diagnostic"}
          </button>
        </div>
      ))}
    </div>
  );
};

const Loader2Local = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" /></svg>
);

/* Personalized Welcome for the School portal — consolidates the diagnostic
   request banner + resumable/approval prompts into one prioritized greeting. */
const SchoolWelcome = ({ activeDiag, session, canApprove, schoolName, onStarted }) => {
  const navigate = useNavigate();
  const [reqs, setReqs] = useState([]);
  useEffect(() => {
    (async () => {
      const h = getHdrs(); if (!h) return;
      try {
        const r = await axios.get(`${API}/api/diagnostics/period-requests`, { headers: h });
        setReqs(r.data?.requests || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const start = async (req) => {
    try {
      await axios.post(`${API}/api/diagnostics/period-requests/${req.id}/start`, {}, { headers: getHdrs() });
      if (onStarted) await onStarted();
      navigate("/diagnostic/form");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start the diagnostic"); }
  };

  const firstName = (session?.user?.full_name || session?.user?.name || session?.user?.email || "").split(/[ @]/)[0];
  const school = schoolName || "your school";
  const items = [];

  // School Admin — a submission is awaiting your approval (highest priority).
  if (canApprove && activeDiag) {
    items.push({ signature: `school_approve:${activeDiag.id}`, priority: 1,
      lead: `A ${activeDiag.period_label || "Diagnostic"} submission for ${school} is awaiting your approval.`,
      actions: [{ label: "Review submission", primary: true, onClick: () => navigate("/diagnostic/form") }] });
  }
  // Period requests pushed by the portfolio.
  for (const req of reqs) {
    const inProgress = req.status === "started";
    items.push({
      signature: `school_request:${req.id}:${req.status}`,
      priority: inProgress ? 4 : 2,
      lead: inProgress
        ? `A ${req.period_name} Diagnostic is in progress for ${school}.`
        : `A ${req.period_name} Diagnostic has been requested for ${school} by ${req.portfolio_name || "your portfolio"}.`,
      dateText: req.due_date ? `Due ${req.due_date}.` : "",
      actions: [{ label: inProgress ? "Continue Diagnostic" : "Start Diagnostic", primary: true, onClick: () => start(req) }],
    });
  }
  // A resumable diagnostic that isn't already covered by a request row.
  if (activeDiag && activeDiag.status === "in_progress" && !reqs.some((r) => r.diagnostic_id === activeDiag.id)) {
    items.push({ signature: `school_resume:${activeDiag.id}`, priority: 4,
      lead: `Pick up where you left off on the ${activeDiag.period_label || "current"} Diagnostic.`,
      actions: [{ label: "Continue Diagnostic", primary: true, onClick: () => navigate("/diagnostic/form") }] });
  }
  items.sort((a, b) => a.priority - b.priority);

  return <WelcomeSection name={firstName} items={items} headers={getHdrs()} testid="school-welcome" />;
};

/* ── Dashboard ── */
export const DiagnosticDashboard = () => {
  useRequireAuth();
  const navigate = useNavigate();
  const session = getSession();
  const [history, setHistory] = useState([]);
  const [features, setFeatures] = useState({ whatif_enabled: true, history_enabled: true, approval_mode: "secondary" });
  const [activeDiag, setActiveDiag] = useState(null);   // latest non-locked
  const [activeSummary, setActiveSummary] = useState(null);
  const [previous, setPrevious] = useState(null);       // previous diagnostic for comparison
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const reload = async () => {
    const h = getHdrs(); if (!h) return;
    setLoading(true);
    try {
      const hist = await axios.get(`${API}/api/diagnostics/history/all`, { headers: h });
      setHistory(hist.data || []);
      axios.get(`${API}/api/tenant-settings/features`, { headers: h })
        .then((r) => r.data?.features && setFeatures(r.data.features))
        .catch(() => { /* keep defaults */ });
      // Pick the latest non-locked OR fall back to the latest approved/locked
      const arr = hist.data || [];
      const nonLocked = arr.filter((d) => !d.locked).slice(-1)[0];
      const latestApproved = arr.filter((d) => d.locked).slice(-1)[0];
      const active = nonLocked || latestApproved || null;
      if (active) {
        const [full, comp] = await Promise.all([
          axios.get(`${API}/api/diagnostics/${active.id}`, { headers: h }),
          axios.get(`${API}/api/diagnostics/${active.id}/comparison`, { headers: h }).catch(() => ({ data: { previous: null } })),
        ]);
        setActiveDiag(full.data.diagnostic);
        setActiveSummary(full.data.summary);
        setPrevious(comp.data?.previous || null);
      } else {
        setActiveDiag(null);
        setActiveSummary(null);
        setPrevious(null);
      }
    } catch (e) {
      toast.error("Failed to load workspace");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (loading || !activeDiag || window.location.hash !== "#results") return;
    requestAnimationFrame(() => document.getElementById("diag-results")?.scrollIntoView({ block: "start" }));
  }, [loading, activeDiag]);

  const submitForApproval = async () => {
    if (!activeDiag) return;
    try {
      const r = await axios.post(`${API}/api/diagnostics/${activeDiag.id}/submit`, {}, { headers: getHdrs() });
      if (r.data?.finalized) {
        toast.success("Diagnostic completed and finalized.");
        if (sealEligible()) { navigate(`/diagnostic/${activeDiag.id}/complete`); return; }
      }
      else toast.success("Submitted for approval. A different School Admin must approve.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const approve = async () => {
    if (!activeDiag) return;
    try {
      await axios.post(`${API}/api/diagnostics/${activeDiag.id}/approve`, { notes: "" }, { headers: getHdrs() });
      toast.success("Diagnostic approved and locked.");
      if (sealEligible()) { navigate(`/diagnostic/${activeDiag.id}/complete`); return; }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const sendBack = async () => {
    if (!activeDiag) return;
    try {
      await axios.post(`${API}/api/diagnostics/${activeDiag.id}/send-back`, { notes: "" }, { headers: getHdrs() });
      toast.success("Sent back to editing.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const downloadReport = async (diagId, periodLabel) => {
    try {
      const r = await axios.get(`${API}/api/diagnostics/${diagId}/report.pdf`, {
        headers: getHdrs(),
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MySchoolRx - ${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const primaryCtaLabel = useMemo(() => {
    if (!activeDiag) return "Start Diagnostic";
    if (activeDiag.status === "approved") return "Review Diagnostic";
    if (activeDiag.status === "submitted_for_review") return "Review";
    if (activeDiag.status === "completed") return "Review Responses";
    if (activeDiag.status === "in_progress") return "Continue Diagnostic";
    return "Start Diagnostic";
  }, [activeDiag]);

  const canApprove = activeDiag?.status === "submitted_for_review"
    && activeDiag?.submitted_by && activeDiag.submitted_by !== session?.user?.id;
  const submitterIsSelf = activeDiag?.submitted_by === session?.user?.id;

  // The dashboard falls back to the latest approved diagnostic when no in-progress one exists.
  // canStartNew = there is no non-locked diagnostic anywhere (the backend create_diagnostic rule).
  const canStartNew = !history.some((h) => !h.locked);
  const activeIsLocked = !!activeDiag?.locked;

  if (loading) {
    return <Shell><div className="px-8 py-20 text-center text-[#6b7280]">Loading dashboard…</div></Shell>;
  }

  return (
    <Shell>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-8" data-testid="diag-dashboard">
        <FoundingPartnerCard />
        <SchoolWelcome
          activeDiag={activeDiag}
          session={session}
          canApprove={canApprove}
          schoolName={session?.tenant?.school_name || session?.tenant?.name}
          onStarted={reload}
        />
        {/* Top action row */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-[#111827]">Diagnostic Workspace</h1>
            {activeDiag && (
              <PeriodLabelLine
                diag={activeDiag}
                locked={activeIsLocked}
                onRename={async (newLabel) => {
                  try {
                    await axios.patch(`${API}/api/diagnostics/${activeDiag.id}`,
                      { period_label: newLabel, period_type: activeDiag.period_type === "initial" ? "custom" : activeDiag.period_type },
                      { headers: getHdrs() });
                    toast.success("Period renamed");
                    reload();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
                }}
              />
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => setShowInvite(true)} variant="outline" className="border-[#e5e7eb] text-[#111827]" data-testid="diag-invite-open">
              <UserPlus className="h-4 w-4 mr-1.5" /> Invite Users
            </Button>
            {canStartNew && (
              <Button onClick={() => setShowNew(true)} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="diag-new-open">
                <Plus className="h-4 w-4 mr-1.5" /> New Diagnostic
              </Button>
            )}
          </div>
        </div>

        <div className="mb-5">
          <SandboxPanel
            authFetch={(url, opts = {}) => fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(getHdrs() || {}) } })}
            onChanged={reload}
          />
        </div>

        <IntelligencePanel
          storageKey="school"
          authFetch={(url, opts = {}) => fetch(url, { ...opts, headers: { ...(opts.headers || {}), ...(getHdrs() || {}) } })}
          getContext={() => ({ page: "diagnostic_dashboard", school: activeDiag?.tenant_id,
            period: activeDiag?.period_label, overall_score: activeSummary?.score,
            risk_band: activeSummary?.overall_risk_band, date: new Date().toISOString().slice(0, 10) })}
        />

        {!activeDiag && history.length === 0 && (
          <div className="rounded-2xl border border-[#e5e7eb] bg-white p-10 text-center">
            <p className="text-[15px] text-[#4b5563]">No diagnostic yet. Start one to begin.</p>
          </div>
        )}

        {/* Active diagnostic — reordered per 6/3 dashboard spec:
            (3) Overall Risk → (4) Heat Map → (5) Warning Lights →
            (6) Board-Ready Report → (7) Approval/Lock Banner →
            (8) Summary Stats + actions → (9) History (below) →
            (10) AI Plan → (11) What-If → (12) Support/Services footer. */}
        {activeDiag && (
          <>
            {/* 3. Overall Risk Level */}
            <div id="diag-results" className="scroll-mt-4" data-testid="diag-results-anchor" />
            {previous && previous.scoring_version !== previous.current_scoring_version && <ScoringVersionNote testId="diag-scoring-version-note" />}
            <OverallRiskBanner summary={activeSummary} diagnostic={activeDiag} prevScore={previous?.summary?.overall_score} />

            {/* 4. Pillar Health Heat Map (v2, shared with Portfolio) */}
            <div className="mt-6">
              <PillarHeatMapV2
                viewer="school"
                pillars={(activeSummary?.category_summaries || []).map((c) => ({
                  ...c, prev_score: previous?.summary?.category_summaries?.find((x) => x.id === c.id)?.score ?? null,
                }))}
                gridTestId="diag-heat-map"
                ids={{ card: (p) => `diag-cat-card-${p.id}`, strip: (p) => `diag-cat-heat-${p.id}`, open: (p) => `diag-cat-continue-${p.id}`,
                  chip: (p) => `pillar-chip-${p.id}`, prev: (p) => `diag-cat-prev-${p.id}` }}
                onOpen={(p) => navigate(`/diagnostic/form?cat=${p.id}`)}
                onAddEvidence={(p) => navigate(`/diagnostic/form?cat=${p.id}`)}
                extra={(p) => (
                  <ExplainButton
                    question={`Explain my "${p.title}" pillar: why is the score ${p.score ?? "what it is"}, what's driving it, and what should I improve first?`}
                    context={{ pillar: p.title, pillar_id: p.id, pillar_score: p.score, risk_level: p.risk_level }}
                  />
                )}
              />
            </div>

            {/* 5. Warning Lights — surfaces urgent issues OR onboarding message. */}
            <WarningLightsSection
              summary={activeSummary}
              diagnostic={activeDiag}
              onJumpToCategory={(cid) => navigate(`/diagnostic/form?cat=${cid}`)}
            />

            {/* 6. Board-Ready Report panel */}
            <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-base font-bold text-[#111827] flex items-center gap-1.5">
                    <Download className="h-4 w-4" /> Board-Ready Report
                  </h3>
                  <p className="text-sm text-[#6b7280]">
                    {activeDiag.report_generated_at
                      ? `Last generated ${new Date(activeDiag.report_generated_at).toLocaleString()}`
                      : "Available once diagnostic is completed."}
                  </p>
                </div>
                <Button
                  disabled={!["completed", "submitted_for_review", "approved"].includes(activeDiag.status)}
                  onClick={() => downloadReport(activeDiag.id, activeDiag.period_label)}
                  className="bg-[#0b1220] hover:bg-black text-white disabled:opacity-50"
                  data-testid="diag-download-report"
                >
                  Download Board-Ready Report
                </Button>
              </div>
            </div>

            {/* 7. Approval / Lock banner */}
            {activeIsLocked && (
              <div className="mt-6 rounded-lg border border-[#bfdbfe] bg-[#eff6ff] px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="diag-locked-context-banner">
                <div className="text-[13px] text-[#1e3a8a]">
                  <strong>{activeDiag.period_label}</strong> is approved &amp; locked. Showing it for context, What-If, and Action Plan below. Start a new diagnostic to capture the next period.
                </div>
                <Button onClick={() => setShowNew(true)} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="diag-locked-start-new">
                  <Plus className="h-4 w-4 mr-1.5" /> Start Next Diagnostic
                </Button>
              </div>
            )}

            {/* 8. Summary stats row — Comparing-against line above, action buttons (incl. View Responses) below */}
            <div className="mt-6 rounded-xl border border-[#e5e7eb] bg-white p-5">
              {previous && (
                <div className="mb-3 -mt-1 text-[11px] text-[#6b7280]" data-testid="diag-prev-banner">
                  Comparing against <strong className="text-[#374151]">{previous.period_label}</strong>
                  {previous.summary?.overall_score != null && (
                    <> · overall <strong>{previous.summary.overall_score}/100</strong></>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
                <SummaryStat label="Period" value={activeDiag.period_label} />
                <SummaryStat label="Status" value={STATUS_LABEL[activeDiag.status] || activeDiag.status} />
                <SummaryStat label="Completion" value={`${activeSummary?.completion_percentage ?? 0}%`} />
                <SummaryStat
                  label="Overall Score"
                  value={activeSummary?.overall_score != null ? `${activeSummary.overall_score}/100${activeIsLocked || activeDiag.status === "approved" ? "" : " · Provisional"}` : "—"}
                  prev={previous?.summary?.overall_score}
                  prevLabel={previous?.period_label}
                  current={activeSummary?.overall_score}
                />
                <SummaryStat label="Risk Level" value={BAND_LABELS[bandOf(activeSummary?.overall_score)] || "—"} />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {activeDiag.status !== "submitted_for_review" && !activeIsLocked && (
                  <Button
                    onClick={() => navigate(`/diagnostic/form?diag=${activeDiag.id}`)}
                    className="bg-[#007a9e] hover:bg-[#00607d] text-white"
                    data-testid="diag-dashboard-primary-cta"
                  >
                    {primaryCtaLabel} <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {activeIsLocked && (
                  <Button onClick={() => navigate("/diagnostic/form")} variant="outline" className="border-[#e5e7eb] text-[#111827]" data-testid="diag-dashboard-view-readonly">
                    View Responses (Read-only) <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {activeDiag.status === "completed" && (
                  <Button onClick={submitForApproval} variant="outline" className="border-[#007a9e] text-[#00698a]" data-testid="diag-submit-approval">
                    {features.approval_mode === "single" ? "Complete & Finalize" : "Submit for Approval"}
                  </Button>
                )}
                {activeDiag.status === "completed" && (
                  <span className="text-[11px] text-[#6b7280]" data-testid="diag-approval-mode-hint">
                    {features.approval_mode === "single"
                      ? "This finalizes the Diagnostic immediately — no second approver required."
                      : "This sends the Diagnostic to Awaiting Approval; a different School Admin must approve it."}
                  </span>
                )}
                {activeDiag.status === "submitted_for_review" && submitterIsSelf && (
                  <div className="text-[12px] text-[#a16207] bg-[#fefce8] border border-[#fde047] rounded-md px-3 py-2">
                    You submitted this. A <strong>different</strong> School Admin must approve (separation of duties).
                  </div>
                )}
                {canApprove && (
                  <>
                    <Button onClick={approve} className="bg-[#059669] hover:bg-[#047857] text-white" data-testid="diag-approve">
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve & Lock
                    </Button>
                    <Button onClick={sendBack} variant="outline" className="border-[#e5e7eb] text-[#111827]" data-testid="diag-send-back">
                      Send Back for Edits
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        {/* 9. Diagnostic History + trend — 6/3 update: hidden until ≥2 approved
            diagnostics exist; only approved diagnostics are plotted. */}
        {(() => {
          if (!features.history_enabled) return null;
          const approvedHistory = (history || []).filter((h) => h.status === "approved" || h.locked);
          if (approvedHistory.length < 1) return null;
          return (
            <div className="mt-8">
              <DiagnosticHistoryV2 history={approvedHistory} viewer="school"
                ids={{ section: "diag-history-section", chart: "diag-trend-chart", list: "diag-history-list", row: (h) => `diag-history-${h.id}` }}
                headerRight={canStartNew && history.length > 0 ? (
                  <Button onClick={() => setShowNew(true)} size="sm" className="min-h-[44px] bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="diag-new-from-history">
                    <Plus className="h-4 w-4 mr-1.5" /> Start New
                  </Button>
                ) : !canStartNew ? (
                  <Button size="sm" variant="outline" className="border-[#e5e7eb] text-[#9ca3af]" disabled>
                    <Plus className="h-4 w-4 mr-1.5" /> New (finish current first)
                  </Button>
                ) : null}
                actions={(h) => (
                  <button onClick={() => downloadReport(h.id, h.period_label)} className="min-h-[44px] text-[12.5px] font-semibold text-[#1a56db] hover:underline" data-testid={`diag-history-download-${h.id}`}>
                    Download PDF
                  </button>
                )} />
            </div>
          );
        })()}

        {/* 10–12. AI Corrective Action Plan → What-If Simulator → Support / Services footer */}
        {activeDiag && (
          <>
            {/* 10. AI Corrective Action Plan (gated, paid upgrade, only after approval) */}
            {activeDiag.status === "approved" && (
              <AICorrectiveActionPlanGate diagnosticId={activeDiag.id} />
            )}

            {/* 11. What-If scenario simulator */}
            {features.whatif_enabled && activeSummary?.total_answered > 0 && (
              <WhatIfPanel diagnosticId={activeDiag.id} baselineSummary={activeSummary} />
            )}

            {/* 12. Support / Services footer */}
            <ServicesCTACard summary={activeSummary} diagnostic={activeDiag} />
          </>
        )}
      </div>

      <InviteUsersModal open={showInvite} onClose={() => setShowInvite(false)} onChanged={reload} />
      <NewDiagnosticModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={reload}
        existingPeriods={history.map((h) => h.period_label)}
      />
    </Shell>
  );
};

const SummaryStat = ({ label, value, prev, prevLabel, current }) => {
  let delta = null;
  if (prev != null && current != null && typeof prev === "number" && typeof current === "number") {
    delta = Math.round((current - prev) * 10) / 10;
  }
  return (
    <div data-testid={`diag-summary-stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#6b7280] font-semibold">{label}</div>
      <div className="mt-1 text-[15px] font-bold text-[#111827] capitalize leading-tight">{value}</div>
      {prev != null && (
        <div className="mt-1 flex items-center gap-1.5 text-[11px]">
          <span className="text-[#6b7280]">Prev: <strong className="text-[#374151]">{prev}</strong></span>
          {delta != null && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${
              delta > 0 ? "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]"
              : delta < 0 ? "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]"
              : "text-[#6b7280] bg-[#f9fafb] border-[#e5e7eb]"
            }`} title={prevLabel ? `vs ${prevLabel}` : ""}>
              {delta > 0 ? "+" : ""}{delta}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Form ── */
import DiagnosticV1 from "./DiagnosticV1";
import { CompletionSeal } from "@/components/CompletionSeal";

// School users only (never portfolio-acting or admin impersonation); backend re-checks.
const sealEligible = () => {
  const s = getSession();
  return !!s && s.acting_as !== "portfolio" && !getImpersonation().active
    && ["tenant_admin", "client", "contributor"].includes(s.user?.role || "tenant_admin");
};
const sealClaims = {};

export const DiagnosticCompletion = () => {
  useRequireAuth();
  const navigate = useNavigate();
  const { periodId } = useParams();
  const [data, setData] = useState(null);
  const toResults = useCallback(() => navigate("/diagnostic#results", { replace: true }), [navigate]);
  const toDashboard = useCallback(() => navigate("/diagnostic", { replace: true }), [navigate]);
  useEffect(() => {
    const h = getHdrs(); if (!h) return;
    sealClaims[periodId] = sealClaims[periodId]
      || axios.post(`${API}/api/diagnostics/${periodId}/completion-seal`, {}, { headers: h }).then((r) => r.data).catch(() => ({ show: false }));
    sealClaims[periodId].then((d) => (d.show ? setData(d) : toResults()));
  }, [periodId, toResults]);
  useEffect(() => () => { if (data) sealClaims[periodId] = Promise.resolve({ show: false }); }, [data, periodId]);
  return <Shell>{data && <CompletionSeal data={data} onResults={toResults} onDashboard={toDashboard} />}</Shell>;
};

export const DiagnosticForm = () => {
  useRequireAuth();
  const navigate = useNavigate();
  const [v1DiagId, setV1DiagId] = useState(null);
  const [decided, setDecided] = useState(false);

  useEffect(() => {
    (async () => {
      const h = getHdrs(); if (!h) return;
      const params = new URLSearchParams(window.location.search);
      const explicitId = params.get("diag");
      try {
        // Explicit diagnostic id (e.g. a Portfolio-perspective assessment opened
        // from Diagnostic Periods) — load that specific one, not the tenant's active.
        if (explicitId) {
          const r = await axios.get(`${API}/api/diagnostics/${explicitId}`, { headers: h });
          const d = r.data?.diagnostic;
          if (d && d.question_set_id === "caporilli_v1_final") { setV1DiagId(d.id); }
          setDecided(true);
          return;
        }
        const list = await axios.get(`${API}/api/diagnostics`, { headers: h });
        const arr = list.data || [];
        const d = arr.find((x) => !x.locked) || arr[0];
        if (!d) {
          toast.error("No diagnostic available. Create one from the dashboard.");
          navigate("/diagnostic");
          return;
        }
        if (d.question_set_id === "caporilli_v1_final") {
          setV1DiagId(d.id);
        }
        setDecided(true);
      } catch {
        setDecided(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (v1DiagId) {
    return <Shell><DiagnosticV1 diagnosticId={v1DiagId} /></Shell>;
  }
  if (!decided) {
    return <Shell><div className="px-8 py-20 text-center text-[#6b7280]">Loading…</div></Shell>;
  }
  return <Shell><LegacyDiagnosticForm /></Shell>;
};

/* ── Legacy category-based form (kept for diagnostics created before v1) ── */
const LegacyDiagnosticForm = () => {
  const navigate = useNavigate();
  const [diagnostic, setDiagnostic] = useState(null);
  const [summary, setSummary] = useState(null);
  const [responses, setResponses] = useState({});
  const [schema, setSchema] = useState({ categories: [], questions: {}, scale: [] });
  const [activeCat, setActiveCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prevResponses, setPrevResponses] = useState({});   // key "cat::idx" → previous response
  const [prevDiag, setPrevDiag] = useState(null);

  useEffect(() => {
    (async () => {
      const h = getHdrs();
      if (!h) return;
      try {
        const [schemaR, listR] = await Promise.all([
          axios.get(`${API}/api/diagnostics/categories`),
          axios.get(`${API}/api/diagnostics`, { headers: h }),
        ]);
        setSchema(schemaR.data);
        // Pick most recent non-locked diagnostic
        const arr = listR.data || [];
        const d = arr.find((x) => !x.locked) || arr[0];
        if (!d) {
          toast.error("No diagnostic available. Create one from the dashboard.");
          navigate("/diagnostic");
          return;
        }
        const [full, comp] = await Promise.all([
          axios.get(`${API}/api/diagnostics/${d.id}`, { headers: h }),
          axios.get(`${API}/api/diagnostics/${d.id}/comparison`, { headers: h }).catch(() => ({ data: { previous: null } })),
        ]);
        setDiagnostic(full.data.diagnostic);
        setSummary(full.data.summary);
        const idx = {};
        for (const r of full.data.responses || []) {
          idx[`${r.category}::${r.question_index}`] = r;
        }
        setResponses(idx);
        // Build previous-response lookup
        const prev = comp.data?.previous;
        if (prev) {
          setPrevDiag(prev);
          const pidx = {};
          for (const r of prev.responses || []) {
            pidx[`${r.category}::${r.question_index}`] = r;
          }
          setPrevResponses(pidx);
        }
        const params = new URLSearchParams(window.location.search);
        const cat = params.get("cat") || schemaR.data.categories?.[0]?.id;
        setActiveCat(cat);
      } catch (e) {
        toast.error("Failed to load diagnostic form");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locked = diagnostic?.locked || ["approved", "submitted_for_review"].includes(diagnostic?.status);

  const saveResponse = async (category, qIdx, patch) => {
    if (!diagnostic || locked) return;
    const existing = responses[`${category}::${qIdx}`] || {};
    const body = {
      category,
      question_index: qIdx,
      response_value: patch.response_value !== undefined ? patch.response_value : existing.response_value ?? null,
      notes: patch.notes !== undefined ? patch.notes : existing.notes || "",
      evidence_url: patch.evidence_url !== undefined ? patch.evidence_url : existing.evidence_url || "",
      assigned_owner: patch.assigned_owner !== undefined ? patch.assigned_owner : existing.assigned_owner || "",
      is_not_applicable: patch.is_not_applicable !== undefined ? patch.is_not_applicable : existing.is_not_applicable || false,
    };
    try {
      const r = await axios.put(`${API}/api/diagnostics/${diagnostic.id}/responses`, body, { headers: getHdrs() });
      setResponses((prev) => ({ ...prev, [`${category}::${qIdx}`]: r.data.response }));
      setSummary(r.data.summary);
      setDiagnostic((prev) => ({ ...prev, status: r.data.diagnostic_status }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (loading) {
    return <div className="px-8 py-20 text-center text-[#6b7280]">Loading…</div>;
  }

  const categories = schema.categories;
  const questions = schema.questions[activeCat] || [];
  const catSummary = summary?.category_summaries?.find((c) => c.id === activeCat);

  return (
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-6" data-testid="diag-form">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 bg-[#f9fafb] border-b border-[#e5e7eb] mb-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <button onClick={() => navigate("/diagnostic")} className="text-[12px] text-[#6b7280] hover:text-[#111827]">← Back to dashboard</button>
              <h1 className="text-lg font-bold text-[#111827]">{diagnostic?.period_label || "MySchoolRx"}</h1>
              {prevDiag && (
                <div className="text-[11px] text-[#6b7280]" data-testid="diag-form-prev-banner">
                  Comparing against <strong className="text-[#374151]">{prevDiag.period_label}</strong>
                  {prevDiag.summary?.overall_score != null && <> · overall <strong>{prevDiag.summary.overall_score}/100</strong></>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-4 text-[12px]">
              <div className="text-[#6b7280]">Completion: <span className="font-bold text-[#111827]">{summary?.completion_percentage ?? 0}%</span></div>
              <div className="text-[#6b7280]">Status: <span className="font-bold text-[#111827] capitalize">{STATUS_LABEL[diagnostic?.status] || (diagnostic?.status || "").replace(/_/g, " ")}</span></div>
              {locked && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#065f46] bg-[#ecfdf5] border border-[#a7f3d0] px-2 py-1 rounded">
                  <LockIcon className="h-3 w-3" /> Read only
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-6">
          {/* Category rail */}
          <aside className="lg:sticky lg:top-[80px] lg:self-start" data-testid="diag-cat-rail">
            <div className="rounded-xl border border-[#e5e7eb] bg-white p-3 space-y-1">
              {categories.map((c) => {
                const cs = summary?.category_summaries?.find((x) => x.id === c.id);
                const isActive = c.id === activeCat;
                const complete = cs && cs.completion_pct >= 100;
                return (
                  <button
                    key={c.id}
                    onClick={() => setActiveCat(c.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-[13px] transition-colors flex items-start justify-between gap-2 ${
                      isActive ? "bg-[#0b1220] text-white" : "hover:bg-[#f9fafb] text-[#374151]"
                    }`}
                    data-testid={`diag-rail-${c.id}`}
                  >
                    <span className="font-semibold leading-tight">{c.title}</span>
                    {complete ? (
                      <CheckCircle2 className="h-4 w-4 text-[#10b981] shrink-0" />
                    ) : (
                      <span className={`text-[10px] ${isActive ? "text-white/60" : "text-[#6b7280]"} shrink-0`}>{cs?.completion_pct ?? 0}%</span>
                    )}
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Workspace */}
          <section className="space-y-4">
            <div className="rounded-xl border border-[#e5e7eb] bg-white p-5">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-[#6b7280] font-semibold">Category</div>
                  <h2 className="text-2xl font-bold text-[#111827]">{categories.find((c) => c.id === activeCat)?.title}</h2>
                </div>
                {catSummary && (
                  <div className="flex items-center gap-3 text-[12px]">
                    <span className="text-[#6b7280]">Completion: <strong className="text-[#111827]">{catSummary.completion_pct}%</strong></span>
                    {catSummary.score != null && <span className="text-[#6b7280]">Score: <strong className="text-[#111827]">{catSummary.score}/100</strong></span>}
                    {catSummary.score != null && <RiskChip score={catSummary.score} />}
                  </div>
                )}
              </div>
            </div>

            {questions.map((q, qIdx) => {
              const key = `${activeCat}::${qIdx}`;
              const r = responses[key] || {};
              const prev = prevResponses[key];
              const prevVal = prev?.is_not_applicable ? "NA" : (prev?.response_value ?? null);
              let qDelta = null;
              if (typeof r.response_value === "number" && typeof prev?.response_value === "number" && !prev?.is_not_applicable && !r.is_not_applicable) {
                qDelta = r.response_value - prev.response_value;
              }
              return (
                <div key={qIdx} className="rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid={`diag-q-${activeCat}-${qIdx}`}>
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#f3f4f6] text-[#6b7280] text-xs font-bold shrink-0">{qIdx + 1}</span>
                    <p className="text-[15px] font-semibold text-[#111827] leading-snug flex-1">{q}</p>
                    {prevVal != null && (
                      <div className="shrink-0 flex items-center gap-1.5 text-[10px]" data-testid={`diag-q-prev-${activeCat}-${qIdx}`}>
                        <span className="text-[#6b7280]">Prev:</span>
                        <span className="inline-flex items-center justify-center min-w-[28px] h-6 rounded-md bg-[#f3f4f6] border border-[#e5e7eb] text-[11px] font-bold text-[#374151]">{prevVal}{typeof prevVal === "number" ? "/5" : ""}</span>
                        {qDelta != null && qDelta !== 0 && (
                          <span className={`px-1.5 py-0.5 rounded font-bold border ${
                            qDelta > 0 ? "text-[#065f46] bg-[#ecfdf5] border-[#a7f3d0]"
                            : "text-[#b91c1c] bg-[#fef2f2] border-[#fecaca]"
                          }`}>
                            {qDelta > 0 ? "+" : ""}{qDelta}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {prev?.notes && (
                    <div className="mt-2 ml-10 text-[11px] text-[#6b7280] italic line-clamp-2" data-testid={`diag-q-prev-notes-${activeCat}-${qIdx}`}>
                      Last period's note: "{prev.notes}"
                    </div>
                  )}

                  {/* Response scale */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {schema.scale.map((s) => {
                      const sel = r.response_value === s.value && !r.is_not_applicable;
                      return (
                        <button
                          key={s.value}
                          type="button"
                          disabled={locked}
                          onClick={() => saveResponse(activeCat, qIdx, { response_value: s.value, is_not_applicable: false })}
                          className={`px-3 py-1.5 rounded-md border text-[12px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                            sel ? "bg-[#007a9e] border-[#007a9e] text-white" : "bg-white border-[#e5e7eb] text-[#374151] hover:border-[#007a9e]/40"
                          }`}
                          data-testid={`diag-scale-${activeCat}-${qIdx}-${s.value}`}
                        >
                          {s.value} — {s.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => saveResponse(activeCat, qIdx, { is_not_applicable: true, response_value: null })}
                      className={`px-3 py-1.5 rounded-md border text-[12px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                        r.is_not_applicable ? "bg-[#6b7280] border-[#6b7280] text-white" : "bg-white border-[#e5e7eb] text-[#6b7280] hover:border-[#6b7280]/40"
                      }`}
                      data-testid={`diag-scale-${activeCat}-${qIdx}-na`}
                    >
                      N/A
                    </button>
                  </div>

                  {/* Notes / Evidence / Owner */}
                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1.5fr_1fr_1fr] gap-3">
                    <Textarea
                      defaultValue={r.notes || ""}
                      disabled={locked}
                      onBlur={(e) => saveResponse(activeCat, qIdx, { notes: e.target.value })}
                      placeholder="Notes / context…"
                      className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] min-h-[60px] disabled:opacity-60"
                      data-testid={`diag-notes-${activeCat}-${qIdx}`}
                    />
                    <Input
                      defaultValue={r.evidence_url || ""}
                      disabled={locked}
                      onBlur={(e) => saveResponse(activeCat, qIdx, { evidence_url: e.target.value })}
                      placeholder="Evidence URL / file link"
                      className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] h-10 disabled:opacity-60"
                      data-testid={`diag-evidence-${activeCat}-${qIdx}`}
                    />
                    <Input
                      defaultValue={r.assigned_owner || ""}
                      disabled={locked}
                      onBlur={(e) => saveResponse(activeCat, qIdx, { assigned_owner: e.target.value })}
                      placeholder="Assigned owner"
                      className="bg-white border-[#e5e7eb] text-[#111827] text-[13px] h-10 disabled:opacity-60"
                      data-testid={`diag-owner-${activeCat}-${qIdx}`}
                    />
                  </div>
                </div>
              );
            })}

            {/* Footer nav between categories */}
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                disabled={categories.findIndex((c) => c.id === activeCat) === 0}
                onClick={() => {
                  const i = categories.findIndex((c) => c.id === activeCat);
                  if (i > 0) setActiveCat(categories[i - 1].id);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="border-[#e5e7eb] text-[#111827]"
              >
                ← Previous category
              </Button>
              <div className="text-[11px] text-[#9ca3af] flex items-center gap-1.5">
                {locked ? <><LockIcon className="h-3 w-3" /> Locked — no edits allowed</> : <><Save className="h-3 w-3" /> Changes auto-save on blur</>}
              </div>
              <Button
                onClick={() => {
                  const i = categories.findIndex((c) => c.id === activeCat);
                  if (i < categories.length - 1) setActiveCat(categories[i + 1].id);
                  else navigate("/diagnostic");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="bg-[#007a9e] hover:bg-[#00607d] text-white"
                data-testid="diag-next-category"
              >
                {categories.findIndex((c) => c.id === activeCat) === categories.length - 1 ? "Finish — back to dashboard" : "Next category →"}
              </Button>
            </div>
          </section>
        </div>
      </div>
  );
};

export default DiagnosticDashboard;
